import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWeb3React } from '@web3-react/core';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSolanaWallet } from '../../web3/connectors';
import { truncateAddress } from '../../utils/addressUtils';
import './MobileNavigation.css';

interface NavigationItem {
  name: string;
  href: string;
  isRoute: boolean;
  icon?: string;
}

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  navigationItems: NavigationItem[];
  onWalletConnect?: () => void;
  className?: string;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onClose,
  navigationItems,
  onWalletConnect,
  className = ''
}) => {
  const { isActive, account, connector } = useWeb3React();
  const { connected: solanaConnected, publicKey: solanaPublicKey, disconnect: disconnectSolana, connect: connectSolana } = useSolanaWallet();
  const { currentPage } = useNavigation();
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);

  // Determine which wallet is connected
  const isWalletConnected = isActive || solanaConnected;
  const connectedAddress = account || solanaPublicKey;
  const walletType = solanaConnected ? 'Solana' : isActive ? 'EVM' : null;
  
  // Check if MetaMask is installed (can switch between EVM and Solana)
  const isMetaMaskInstalled = typeof window !== 'undefined' && (window as any).ethereum?.isMetaMask;

  const handleDisconnect = async () => {
    try {
      if (solanaConnected) {
        await disconnectSolana();
      } else if (isActive && connector) {
        // For EVM wallets, we need to properly reset the connection
        try {
          // Clear any stored connection data
          localStorage.removeItem('walletName');
          
          // Try to reset the connector state
          if ((connector as any).resetState) {
            await (connector as any).resetState();
          }
        } catch (evmError) {
          console.log('EVM disconnect method failed:', evmError);
        }
      }
      onClose();
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  };

  const handleSwitchNetwork = async () => {
    try {
      onClose();
      
      if (isActive && !solanaConnected) {
        // Currently on EVM, switch to Solana via MetaMask Snap
        // First disconnect EVM properly
        try {
          localStorage.removeItem('walletName');
          if ((connector as any).resetState) {
            await (connector as any).resetState();
          }
        } catch (e) {
          console.log('EVM disconnect error:', e);
        }
        
        // Small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Then connect Solana
        await connectSolana('MetaMask Snap');
      } else if (solanaConnected && !isActive) {
        // Currently on Solana, switch to EVM
        // First disconnect Solana
        await disconnectSolana();
        
        // Small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Open wallet modal to connect EVM wallet
        if (onWalletConnect) {
          onWalletConnect();
        }
      }
    } catch (error) {
      console.error('Network switch failed:', error);
    }
  };

  // Focus management for accessibility
  useEffect(() => {
    if (isOpen && firstItemRef.current) {
      const timer = setTimeout(() => {
        firstItemRef.current?.focus();
      }, 150); // Wait for animation to start
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Trap focus within the mobile menu
  useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = menuRef.current?.querySelectorAll(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleLinkClick = () => {
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`mobile-navigation-overlay ${className}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleOverlayClick}
          aria-hidden={!isOpen}
        >
          <motion.nav
            ref={menuRef}
            className="mobile-navigation-menu"
            initial={{ x: '100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '100%' }}
            transition={{ 
              type: 'tween',
              duration: 0.3,
              ease: 'easeInOut'
            }}
            id="mobile-navigation-menu"
            role="navigation"
            aria-label="Mobile navigation"
          >
            {/* Header */}
            <div className="mobile-nav-header">
              <h2 className="mobile-nav-title">Navigation</h2>
              <button
                className="mobile-nav-close"
                onClick={onClose}
                aria-label="Close navigation menu"
                type="button"
              >
                <span className="close-icon" aria-hidden="true">×</span>
              </button>
            </div>

            {/* Primary Navigation */}
            <div className="mobile-nav-section">
              <h3 className="mobile-nav-section-title">Main</h3>
              <ul className="mobile-nav-list">
                {navigationItems.map((item, index) => (
                  <motion.li
                    key={item.name}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.3,
                      ease: 'easeInOut'
                    }}
                    className="mobile-nav-item"
                  >
                    {item.isRoute ? (
                      <Link
                        ref={index === 0 ? firstItemRef : undefined}
                        to={item.href}
                        className={`mobile-nav-link ${currentPage === item.href ? 'active' : ''}`}
                        onClick={handleLinkClick}
                        role="menuitem"
                      >
                        <span className="mobile-nav-link-text">{item.name}</span>
                        {currentPage === item.href && (
                          <span className="mobile-nav-active-indicator" aria-hidden="true" />
                        )}
                      </Link>
                    ) : (
                      <a
                        ref={index === 0 ? firstItemRef : undefined}
                        href={item.href}
                        className="mobile-nav-link"
                        onClick={handleLinkClick}
                        role="menuitem"
                      >
                        <span className="mobile-nav-link-text">{item.name}</span>
                      </a>
                    )}
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Search Section */}
            <div className="mobile-nav-section">
              <div className="mobile-nav-search">
                <input
                  type="text"
                  placeholder="Search documentation"
                  className="mobile-nav-search-input"
                  aria-label="Search documentation"
                />
              </div>
            </div>

            {/* Documentation Sections */}
            <div className="mobile-nav-section">
              <h3 className="mobile-nav-section-title">GETTING STARTED</h3>
              <ul className="mobile-nav-list mobile-nav-docs-list">
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/minting-and-redeeming"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">Minting & Redeeming</span>
                  </Link>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/how-to-display-usdp-token-in-wallet"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">How to Display USD.P in Wallet</span>
                  </Link>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/user-guide"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">User Guide</span>
                  </Link>
                </motion.li>
              </ul>
            </div>

            {/* Token Features Section */}
            <div className="mobile-nav-section">
              <h3 className="mobile-nav-section-title">TOKEN FEATURES</h3>
              <ul className="mobile-nav-list mobile-nav-docs-list">
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/token-features-usdp"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">USD.P Token Features</span>
                  </Link>
                </motion.li>
              </ul>
            </div>

            {/* Technical Section */}
            <div className="mobile-nav-section">
              <h3 className="mobile-nav-section-title">TECHNICAL</h3>
              <ul className="mobile-nav-list mobile-nav-docs-list">
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/technical-overview"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">Technical Overview</span>
                  </Link>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.3, ease: 'easeInOut' }}
                  className="mobile-nav-item mobile-nav-doc-item"
                >
                  <Link
                    to="/docs/smart-contract"
                    className="mobile-nav-link mobile-nav-doc-link"
                    onClick={handleLinkClick}
                    role="menuitem"
                  >
                    <span className="mobile-nav-link-text">Smart Contract</span>
                  </Link>
                </motion.li>
              </ul>
            </div>

            {/* Actions */}
            <motion.div
              className="mobile-nav-actions"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: (navigationItems.length + 7) * 0.1,
                duration: 0.3,
                ease: 'easeInOut'
              }}
            >
              {!isWalletConnected ? (
                <>
                  <button
                    className="btn btn-ghost mobile-nav-action-btn"
                    onClick={() => {
                      onWalletConnect?.();
                      onClose();
                    }}
                    type="button"
                  >
                    Connect Wallet
                  </button>
                  <button
                    className="btn btn-primary mobile-nav-action-btn"
                    onClick={() => {
                      onWalletConnect?.();
                      onClose();
                    }}
                    type="button"
                  >
                    Launch App
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(171, 159, 242, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                      Connected {walletType} Wallet
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginTop: '4px' }}>
                      {truncateAddress(connectedAddress || '')}
                    </div>
                  </div>
                  
                  {/* Network Switch Option for MetaMask */}
                  {isMetaMaskInstalled && (
                    <button
                      className="btn btn-primary mobile-nav-action-btn"
                      onClick={handleSwitchNetwork}
                      type="button"
                      style={{
                        background: 'rgba(171, 159, 242, 0.2)',
                        color: '#ab9ff2',
                        marginBottom: '8px'
                      }}
                    >
                      🔄 {isActive && !solanaConnected ? 'Switch to Solana' : 'Switch to EVM'}
                    </button>
                  )}
                  
                  <button
                    className="btn btn-primary mobile-nav-action-btn"
                    onClick={handleDisconnect}
                    type="button"
                    style={{ background: 'rgba(255, 107, 107, 0.2)', color: '#ff6b6b' }}
                  >
                    🔌 Disconnect Wallet
                  </button>
                </>
              )}
            </motion.div>

            {/* Footer */}
            <div className="mobile-nav-footer">
              <p className="mobile-nav-footer-text">
                © 2024 USD.P. All rights reserved.
              </p>
            </div>
          </motion.nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileNavigation;